// Camera.jsx
import { PerspectiveCamera } from "@react-three/drei"
import { useFrame } from "@react-three/fiber"
import { useEffect, useRef } from "react"
import { moveAxis } from "../../../utils/moveObjects"
import * as THREE from 'three'
import { useActsStore } from "../../../store/useActsStore"
import usePointsPath from "../../../utils/usePointsPath"
import { useIsMobile } from "../../../utils/useIsMobile"
import { useDeviceTilt } from "../../../utils/useDeviceTilt"

export default function Camera({ innerRef, off }) {
    const isMobile = useIsMobile()
    const getAngles = useDeviceTilt()

    const rotOffsetRef = useRef({ x: 0, y: 0 })

    const camPos = isMobile ? [1.46, -2.10, 11.72] : [1.46, -2.10, 9.72]
    const lookAtTarget = new THREE.Vector3(0, -2, 8)

    const scrollBoost = useRef(false)
    const lastScrollTime = useRef(0)

    const id = useActsStore(state => state.getCurrentActId())
    const phase = useActsStore(state => state.actsProgress[id]?.phase)

    const shakeTime = useRef(0)
    const canBoost = id === 2 && phase === "hold"

    // FOG
    const fogNear = 11.5
    const fogFar = 20
    const fogFarTarget = 18
    const fogNearTarget = 6
    const fogInitialColor = '#c7e9ff'
    const fogBoostedColor = '#ffc7f8'
    const fogInitial = useRef(new THREE.Color(fogInitialColor))
    const fogBoosted = useRef(new THREE.Color(fogBoostedColor))

    const FPS_REF = 60

    function alphaFromSpeed(speed, delta) {
        return 1 - Math.pow(1 - speed, delta * FPS_REF)
    }


    useEffect(() => {
        if (!canBoost) return

        const onWheel = () => {
            scrollBoost.current = true
            lastScrollTime.current = performance.now()
        }

        window.addEventListener("wheel", onWheel, { passive: true })
        return () => window.removeEventListener("wheel", onWheel)

    }, [id, phase])

    const timer = useRef(0)


    // 🔹 init : position + lookAt
    useEffect(() => {
        if (!innerRef.current) return
        innerRef.current.position.set(...camPos)
        innerRef.current.lookAt(lookAtTarget)
    }, [])

    useFrame((_, delta) => {

        const stateActe = useActsStore.getState()
        const id = stateActe.getCurrentActId()
        const holdProgress = stateActe.actsProgress[id]?.holdProgress ?? 0

        if (!innerRef.current) return
        const cam = innerRef.current
        cam.rotation.x -= rotOffsetRef.current.x
        cam.rotation.y -= rotOffsetRef.current.y

        const BOOST_DURATION = 800
        timer.current += delta

        const stillInBoostWindow = performance.now() - lastScrollTime.current <= BOOST_DURATION
        const isBoosting = scrollBoost.current && holdProgress >= 0.01 && stillInBoostWindow || holdProgress >= 0.6

        if (!stillInBoostWindow) {
            scrollBoost.current = false
        }

        // Z + fog
        if (!off.fog) {
            cam.lookAt(lookAtTarget)
            return
        }


        if (isBoosting) {
            moveAxis(innerRef, "z", 14, 0.1, "easeInOut", 5, delta)

            const aFogBoost = alphaFromSpeed(0.08, delta)

            off.fog.far = THREE.MathUtils.lerp(off.fog.far, fogFarTarget, aFogBoost)
            off.fog.near = THREE.MathUtils.lerp(off.fog.near, fogNearTarget, aFogBoost)
            off.fog.color.lerp(fogBoosted.current, aFogBoost)
        } else {
            moveAxis(innerRef, "z", camPos[2], 0.08, "easeIn", 10, delta)
            const aFog = alphaFromSpeed(0.02, delta)
            const aFogColor = alphaFromSpeed(0.03, delta)

            off.fog.far = THREE.MathUtils.lerp(off.fog.far, fogFar, aFog)
            off.fog.near = THREE.MathUtils.lerp(off.fog.near, fogNear, aFog)
            off.fog.color.lerp(fogInitial.current, aFogColor)
        }

        // TREMBLEMENT : uniquement en position
        if (isBoosting) {
            shakeTime.current += delta

            const freq = 20
            const ampPos = 0.08

            const sx = Math.sin(shakeTime.current * freq) * ampPos
            const sy = Math.cos(shakeTime.current * freq * 1.3) * ampPos

            cam.position.x = camPos[0] + sx
            cam.position.y = camPos[1] + sy
        } else {
            shakeTime.current = 0

            const aCamReturn = alphaFromSpeed(0.1, delta)

            cam.position.x = THREE.MathUtils.lerp(cam.position.x, camPos[0], aCamReturn)
            cam.position.y = THREE.MathUtils.lerp(cam.position.y, camPos[1], aCamReturn)
        }

        // 🔹 orientation : toujours vers la même target
        cam.lookAt(lookAtTarget)

        // ---- GYRO ROTATION (mobile only) ----
        if (isMobile) {
            const smooth = 1 - Math.exp(-14 * delta)

            const a = getAngles()

            // pitch = beta, roll = gamma (radians)
            const pitchNeutral = THREE.MathUtils.degToRad(50)
            const pitchDelta = a.pitch - pitchNeutral

            const pitch = THREE.MathUtils.clamp(pitchDelta, -0.6, 0.6)
            const roll = THREE.MathUtils.clamp(a.roll, -0.6, 0.6)

            // amplitudes
            const ampX = 0.3
            const ampY = 0.3

            // ⚠️ mapping que tu avais validé chez toi (axes croisés)
            const rxTarget = pitch * ampX
            const ryTarget = -roll * ampY

            rotOffsetRef.current.x = THREE.MathUtils.lerp(rotOffsetRef.current.x, rxTarget, smooth)
            rotOffsetRef.current.y = THREE.MathUtils.lerp(rotOffsetRef.current.y, ryTarget, smooth)

            // applique l'offset
            cam.rotation.x += rotOffsetRef.current.x
            cam.rotation.y += rotOffsetRef.current.y
        } else {
            // reset propre desktop (évite de garder un offset si resize)
            rotOffsetRef.current.x = 0
            rotOffsetRef.current.y = 0
        }

        let t = (holdProgress - 0.8) / (1.0 - 0.8)
        t = THREE.MathUtils.clamp(t, 0, 1)
        off.fog.near = THREE.MathUtils.lerp(off.fog.near, -9, t)



    })

    return (
        <PerspectiveCamera
            ref={innerRef}
            position={camPos}
            fov={64}
            near={0.1}
        />
    )
}
